import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Mail, Lock, User, Briefcase, Home, Store, Building2, Hash, MapPin, Phone, Smartphone, ShieldCheck, MailCheck } from 'lucide-react';
import { USER_TYPES } from '../utils/pricing';
import PostcodeAddressPicker from '../components/PostcodeAddressPicker';
import SEO from '../components/SEO';
import api from '../api/client';

const TYPE_ICONS = { end_user: Home, builder: Briefcase, retailer: Store };

const emptyBusiness = {
  company_name: '', vat_no: '', eori_no: '', postcode: '',
  address: '', contact_no: '', mobile_no: '',
};

const LoginPage = () => {
  const [params] = useSearchParams();
  const roleParam = params.get('role');
  const isTradePoint = roleParam === 'retailer' || roleParam === 'builder';
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [userType, setUserType] = useState(isTradePoint ? roleParam : 'end_user');
  const [biz, setBiz] = useState(emptyBusiness);
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [pendingVerify, setPendingVerify] = useState(null); // email pending verification
  const [needsVerifyEmail, setNeedsVerifyEmail] = useState(null); // set when login is blocked by 403
  const { login, register, resendVerification } = useAuth();
  const navigate = useNavigate();
  const redirect = params.get('redirect') || '/';

  const isTradeTier = userType === 'builder' || userType === 'retailer';

  const setBizField = (k, v) => setBiz(prev => ({ ...prev, [k]: v }));

  const handleResend = async (targetEmail) => {
    try {
      await resendVerification(targetEmail);
      toast.success(`Verification link re-sent to ${targetEmail}`);
    } catch (err) {
      toast.error('Could not resend verification email');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNeedsVerifyEmail(null);
    try {
      if (mode === 'login') {
        await login(email, password);
        toast.success('Welcome back!');
        navigate(redirect);
      } else {
        const extras = { ...(isTradeTier ? biz : {}), newsletter_opt_in: newsletterOptIn };
        const result = await register(name, email, password, userType, extras);
        if (result?.verification_sent) {
          setPendingVerify(email);
          return;
        }
        if (isTradeTier) {
          toast.success('Account created — awaiting admin approval for trade pricing.');
        } else {
          toast.success('Account created!');
        }
        navigate(redirect);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      if (status === 403 && typeof detail === 'string' && detail.toLowerCase().includes('verify')) {
        setNeedsVerifyEmail(email);
      }
      toast.error(typeof detail === 'string' ? detail : (err.message || 'Something went wrong'));
    } finally { setLoading(false); }
  };

  // Post-registration screen: "check your inbox".
  if (pendingVerify) {
    return (
      <div className="max-w-md mx-auto px-4 py-16">
        <SEO title="Check your inbox" path="/login" noindex />
        <div className="bg-white border border-neutral-200 rounded-sm p-8 text-center" data-testid="verify-inbox-screen">
          <MailCheck className="w-14 h-14 text-[#F46A25] mx-auto mb-4" />
          <h1 className="text-2xl font-black text-[#12263A] mb-2">Check your inbox</h1>
          <p className="text-sm text-neutral-600 mb-6">
            We've sent a verification link to <strong>{pendingVerify}</strong>.
            Click it to activate your account — then you can sign in and start shopping.
          </p>
          <button
            onClick={() => handleResend(pendingVerify)}
            className="text-sm text-[#F46A25] hover:underline font-semibold"
            data-testid="verify-resend-btn"
          >
            Didn't get it? Resend the email
          </button>
          <div className="mt-6">
            <Link to="/" className="text-sm text-[#12263A] hover:text-[#F46A25]">← Back to home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`mx-auto px-4 py-10 ${mode === 'register' && isTradeTier ? 'max-w-2xl' : 'max-w-md'}`}>
      <SEO
        title={mode === 'login' ? 'Sign in' : 'Create an account'}
        description="Sign in to your Dr Tools account or create a new personal, builder or retailer account to unlock trade pricing."
        path="/login"
        noindex
      />
      <div className="bg-white border border-neutral-200 rounded-sm p-6 md:p-8">
        {isTradePoint && (
          <div className="bg-[#12263A] text-white text-center py-3 -mx-6 -mt-6 md:-mx-8 md:-mt-8 mb-5 rounded-t-sm">
            <div className="text-[10px] uppercase tracking-widest text-[#F46A25] font-bold">Dr Tools Trade Point</div>
            <div className="text-sm font-semibold">Trade prices · Trade service · Trade credit</div>
          </div>
        )}
        <div className="flex mb-6 border-b border-neutral-200">
          <button onClick={() => setMode('login')} data-testid="login-tab-signin" className={`flex-1 py-3 text-sm font-bold transition-colors ${mode === 'login' ? 'text-[#F46A25] border-b-2 border-[#F46A25]' : 'text-neutral-500 hover:text-[#12263A]'}`}>Sign in</button>
          <button onClick={() => setMode('register')} data-testid="login-tab-register" className={`flex-1 py-3 text-sm font-bold transition-colors ${mode === 'register' ? 'text-[#F46A25] border-b-2 border-[#F46A25]' : 'text-neutral-500 hover:text-[#12263A]'}`}>Create account</button>
        </div>

        <h1 className="text-2xl font-black text-[#12263A] mb-2">
          {isTradePoint ? (mode === 'login' ? 'Trade Point sign in' : 'Open a Trade Point account') : (mode === 'login' ? 'Welcome back' : 'Join Dr Tools')}
        </h1>
        <p className="text-sm text-neutral-600 mb-6">
          {isTradePoint
            ? (mode === 'login' ? 'Sign in with your retailer account to access trade prices and credit.' : 'Register as a retailer to unlock wholesale pricing and pay-on-credit options.')
            : (mode === 'login' ? 'Sign in to access your basket and orders.' : 'Create an account to start shopping.')}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <>
              <div>
                <label className="block text-sm font-semibold text-[#12263A] mb-1">Full name</label>
                <div className="relative">
                  <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                  <input value={name} onChange={e => setName(e.target.value)} type="text" required placeholder="John Doe" data-testid="register-name-input" className="w-full border border-neutral-300 rounded-sm py-2.5 pl-10 pr-3 text-sm focus:outline-none focus:border-[#F46A25]" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#12263A] mb-2">I am a...</label>
                <div className="grid grid-cols-3 gap-2">
                  {USER_TYPES.map(t => {
                    const Icon = TYPE_ICONS[t.value] || User;
                    const active = userType === t.value;
                    return (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => setUserType(t.value)}
                        data-testid={`register-usertype-${t.value}`}
                        className={`p-3 border-2 rounded-sm text-center transition-colors ${active ? 'border-[#F46A25] bg-orange-50' : 'border-neutral-200 hover:border-neutral-300'}`}
                      >
                        <Icon className={`w-5 h-5 mx-auto mb-1 ${active ? 'text-[#F46A25]' : 'text-neutral-500'}`} />
                        <div className={`text-xs font-bold ${active ? 'text-[#F46A25]' : 'text-[#12263A]'}`}>{t.short}</div>
                        <div className="text-[10px] text-neutral-500 leading-tight mt-0.5">{t.description}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {isTradeTier && (
                <div className="bg-orange-50/40 border border-orange-200 rounded-sm p-4 space-y-3" data-testid="register-business-panel">
                  <div className="flex items-start gap-2 text-[11px] text-[#12263A]">
                    <ShieldCheck className="w-4 h-4 text-[#F46A25] shrink-0 mt-0.5" />
                    <span>
                      <strong>Admin approval required.</strong> You'll be able to sign in immediately, but {userType === 'builder' ? 'Builder' : 'Retailer'} trade discounts and credit are unlocked only after our team verifies your business details below.
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <TextField label="Company name" icon={Building2} value={biz.company_name} onChange={v => setBizField('company_name', v)} testid="register-company-input" required />
                    <TextField label="VAT number" icon={Hash} value={biz.vat_no} onChange={v => setBizField('vat_no', v)} testid="register-vat-input" required />
                    <TextField label="Company registration number" icon={Hash} value={biz.eori_no} onChange={v => setBizField('eori_no', v)} testid="register-companyreg-input" required />
                    <TextField label="Contact number" icon={Phone} value={biz.contact_no} onChange={v => setBizField('contact_no', v)} testid="register-contact-input" required />
                    <TextField label="Mobile number" icon={Smartphone} value={biz.mobile_no} onChange={v => setBizField('mobile_no', v)} testid="register-mobile-input" required />
                  </div>
                  <div className="border border-orange-200 rounded-sm bg-white p-3">
                    <div className="text-[11px] font-semibold text-[#12263A] mb-2 uppercase tracking-wide flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 text-[#F46A25]" /> Business address (enter UK postcode to auto-fill)
                    </div>
                    <PostcodeAddressPicker
                      showNamePhone={false}
                      onSelect={(addr) => {
                        if (!addr) return;
                        const line = [addr.line1, addr.line2, addr.city].filter(Boolean).join(', ');
                        setBiz(prev => ({
                          ...prev,
                          postcode: (addr.postcode || prev.postcode).toUpperCase(),
                          address: line || prev.address,
                        }));
                      }}
                    />
                    {(biz.address || biz.postcode) && (
                      <div className="mt-2 text-[11px] text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-sm px-2 py-1.5" data-testid="register-address-preview">
                        <b>Selected:</b> {biz.address || '—'} {biz.postcode && <span className="ml-1 font-mono">{biz.postcode}</span>}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          <div>
            <label className="block text-sm font-semibold text-[#12263A] mb-1">Email</label>
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input value={email} onChange={e => setEmail(e.target.value)} type="email" required placeholder="you@example.com" data-testid="login-email-input" className="w-full border border-neutral-300 rounded-sm py-2.5 pl-10 pr-3 text-sm focus:outline-none focus:border-[#F46A25]" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-semibold text-[#12263A]">Password</label>
              {mode === 'login' && (
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  data-testid="login-forgot-password-link"
                  className="text-xs font-semibold text-[#F46A25] hover:underline"
                >
                  Forgot your password?
                </button>
              )}
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input value={password} onChange={e => setPassword(e.target.value)} type="password" required minLength={mode === 'register' ? 8 : 6} placeholder={mode === 'register' ? 'Min 8 chars (letter + number)' : 'Password'} data-testid="login-password-input" className="w-full border border-neutral-300 rounded-sm py-2.5 pl-10 pr-3 text-sm focus:outline-none focus:border-[#F46A25]" />
            </div>
          </div>

          {mode === 'register' && (
            <label className="flex items-start gap-2 cursor-pointer select-none pt-1" data-testid="register-newsletter-wrap">
              <input
                type="checkbox"
                checked={newsletterOptIn}
                onChange={e => setNewsletterOptIn(e.target.checked)}
                data-testid="register-newsletter-checkbox"
                className="mt-0.5 w-4 h-4 accent-[#F46A25]"
              />
              <span className="text-xs text-neutral-700 leading-relaxed">
                Send me offers, product updates and Dr Tools trade newsletters. You can unsubscribe at any time.
              </span>
            </label>
          )}

          {needsVerifyEmail && (
            <div className="bg-amber-50 border border-amber-300 rounded-sm p-3 text-xs text-amber-900" data-testid="login-verify-required">
              <div className="font-semibold mb-1">Your email isn't verified yet.</div>
              <button
                type="button"
                onClick={() => handleResend(needsVerifyEmail)}
                className="text-[#F46A25] font-bold hover:underline"
                data-testid="login-resend-verification-btn"
              >
                Resend verification email to {needsVerifyEmail}
              </button>
            </div>
          )}

          <button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold py-3 rounded-sm transition-colors disabled:opacity-60">
            {loading ? 'Please wait...' : (mode === 'login' ? 'Sign in' : 'Create account')}
          </button>
        </form>

        {/* Google sign-in is offered ONLY for end-users (not the trade tiers
            or the /login?role=... TradePoint variant, which need business docs). */}
        {!isTradePoint && (mode === 'login' || userType === 'end_user') && (
          <>
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-neutral-200" />
              <span className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">Or</span>
              <div className="flex-1 h-px bg-neutral-200" />
            </div>
            <button
              type="button"
              onClick={async () => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                // Custom Google OAuth (drtoolsuk.com branded). The redirect_uri
                // is built from window.location.origin so preview + prod both work.
                try {
                  const redirectUri = window.location.origin + '/auth/google';
                  const r = await api.post('/auth/google/start', { redirect_uri: redirectUri });
                  window.location.href = r.data.auth_url;
                } catch (e) {
                  toast.error('Unable to start Google sign-in. Please try again.');
                }
              }}
              data-testid="login-google-btn"
              className="w-full border border-neutral-300 hover:border-[#F46A25] hover:bg-neutral-50 text-[#12263A] font-semibold py-2.5 rounded-sm flex items-center justify-center gap-3 transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              <span>{mode === 'login' ? 'Continue with Google' : 'Sign up with Google'}</span>
            </button>
            <p className="text-[11px] text-neutral-500 mt-2 text-center">
              Fast, one-tap. Trade accounts (builder/retailer) still register with the business form.
            </p>
          </>
        )}

        <div className="mt-6 text-center text-xs text-neutral-500">
          By continuing you agree to our <a href="#" className="text-[#F46A25] hover:underline">Terms</a> and <a href="#" className="text-[#F46A25] hover:underline">Privacy Policy</a>.
        </div>
      </div>

      <div className="text-center mt-4">
        <Link to="/" className="text-sm text-[#12263A] hover:text-[#F46A25]">← Back to home</Link>
      </div>
    </div>
  );
};

const TextField = ({ label, icon: Icon, value, onChange, testid, required }) => (
  <div>
    <label className="block text-[11px] font-semibold text-[#12263A] mb-1 uppercase tracking-wide">{label}</label>
    <div className="relative">
      {Icon && <Icon className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />}
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
        type="text"
        data-testid={testid}
        className="w-full border border-neutral-300 rounded-sm py-2 pl-9 pr-3 text-sm focus:outline-none focus:border-[#F46A25]"
      />
    </div>
  </div>
);

export default LoginPage;
